// ============================================
// INFRASTRUCTURE - Caja Turnos Repository
// ============================================
// Módulo nuevo de Caja 360 (turno_caja / movimiento_caja / caja_configuracion_almacen).
// Coexiste con caja.repository.ts (módulo viejo, /cuadratura-cajas) sin reemplazarlo.

import { axiosClient } from './axios-client';
import type {
  AbrirTurnoDto,
  ActualizarConfiguracionCajaDto,
  CerrarTurnoDto,
  ConfiguracionCaja,
  FiltrosTurnos,
  ForzarCierreTurnoDto,
  MiTurnoResult,
  MovimientoDeshacerLogListResponse,
  MovimientoListResponse,
  RechazarMovimientoDto,
  RegistrarMovimientoDto,
  TurnoCaja,
  TurnoListResponse,
} from '../../domain/dtos';

export const cajaTurnosRepository = {
  /**
   * Abrir mi turno de caja
   * POST /caja/turnos
   */
  async abrirTurno(dto: AbrirTurnoDto): Promise<TurnoCaja> {
    const { data } = await axiosClient.post<TurnoCaja>('/caja/turnos', dto);
    return data;
  },

  /**
   * Mi turno abierto en esta sucursal (turno: null si no tengo ninguno acá),
   * más — solo cuando no hay uno acá — los turnos que tengo abiertos en
   * otras sucursales del mismo negocio, para no dejarlos invisibles.
   * GET /caja/turnos/me
   */
  async obtenerMiTurno(): Promise<MiTurnoResult> {
    const { data } = await axiosClient.get<MiTurnoResult>('/caja/turnos/me');
    return data;
  },

  /**
   * Cerrar un turno (con o sin arqueo)
   * POST /caja/turnos/{id}/cierre
   */
  async cerrarTurno(turnoId: number, dto: CerrarTurnoDto): Promise<TurnoCaja> {
    const { data } = await axiosClient.post<TurnoCaja>(`/caja/turnos/${turnoId}/cierre`, dto);
    return data;
  },

  /**
   * Registrar un movimiento (retiro/gasto/ingreso extra) en un turno
   * POST /caja/turnos/{turnoId}/movimientos
   */
  async registrarMovimiento(turnoId: number, dto: RegistrarMovimientoDto): Promise<void> {
    await axiosClient.post(`/caja/turnos/${turnoId}/movimientos`, dto);
  },

  /**
   * Anular (rechazar) mi propio movimiento
   * DELETE /caja/movimientos/{id}
   */
  async anularMovimiento(movimientoId: number): Promise<void> {
    await axiosClient.delete(`/caja/movimientos/${movimientoId}`);
  },

  /**
   * Movimientos de un turno (cualquier estado)
   * GET /caja/turnos/{id}/movimientos
   */
  async obtenerMovimientosDeTurno(turnoId: number, page: number = 0): Promise<MovimientoListResponse> {
    const { data } = await axiosClient.get<MovimientoListResponse>(
      `/caja/turnos/${turnoId}/movimientos?page=${page}`
    );
    return data;
  },

  /**
   * Configuración de caja del almacén actual
   * GET /caja/configuracion
   */
  async obtenerConfiguracion(): Promise<ConfiguracionCaja> {
    const { data } = await axiosClient.get<ConfiguracionCaja>('/caja/configuracion');
    return data;
  },

  /**
   * Actualizar la configuración de caja (solo propietario)
   * PUT /caja/configuracion
   */
  async actualizarConfiguracion(dto: ActualizarConfiguracionCajaDto): Promise<ConfiguracionCaja> {
    const { data } = await axiosClient.put<ConfiguracionCaja>('/caja/configuracion', dto);
    return data;
  },

  /**
   * Historial de turnos con filtros (propietario ve todos, vendedor solo los suyos)
   * GET /caja/turnos
   */
  async listarTurnos(filtros: FiltrosTurnos): Promise<TurnoListResponse> {
    const params = new URLSearchParams();
    if (filtros.usuarioId !== undefined) params.append('usuarioId', filtros.usuarioId.toString());
    if (filtros.desde) params.append('desde', filtros.desde);
    if (filtros.hasta) params.append('hasta', filtros.hasta);
    if (filtros.estado) params.append('estado', filtros.estado);
    if (filtros.page !== undefined) params.append('page', filtros.page.toString());
    if (filtros.sort) params.append('sort', filtros.sort);

    const { data } = await axiosClient.get<TurnoListResponse>(`/caja/turnos?${params.toString()}`);
    return data;
  },

  /**
   * Turnos abiertos del almacén (propietario) o el propio (vendedor)
   * GET /caja/turnos/abiertos
   */
  async listarTurnosAbiertos(page: number = 0, sort?: string): Promise<TurnoListResponse> {
    const params = new URLSearchParams({ page: page.toString() });
    if (sort) params.append('sort', sort);
    const { data } = await axiosClient.get<TurnoListResponse>(`/caja/turnos/abiertos?${params.toString()}`);
    return data;
  },

  /**
   * Detalle de un turno (cualquier estado), del almacén del usuario
   * GET /caja/turnos/{id}
   */
  async obtenerTurnoPorId(turnoId: number): Promise<TurnoCaja> {
    const { data } = await axiosClient.get<TurnoCaja>(`/caja/turnos/${turnoId}`);
    return data;
  },

  /**
   * Cierre forzado de un turno ajeno (solo propietario)
   * POST /caja/turnos/{id}/cierre-forzado
   */
  async forzarCierreTurno(turnoId: number, dto: ForzarCierreTurnoDto): Promise<TurnoCaja> {
    const { data } = await axiosClient.post<TurnoCaja>(`/caja/turnos/${turnoId}/cierre-forzado`, dto);
    return data;
  },

  /**
   * Movimientos pendientes de aprobación del almacén (solo propietario)
   * GET /caja/movimientos/pendientes
   */
  async listarMovimientosPendientes(page: number = 0, sort?: string): Promise<MovimientoListResponse> {
    const params = new URLSearchParams({ page: page.toString() });
    if (sort) params.append('sort', sort);
    const { data } = await axiosClient.get<MovimientoListResponse>(`/caja/movimientos/pendientes?${params.toString()}`);
    return data;
  },

  /**
   * Cantidad de movimientos pendientes de aprobación (solo propietario). Solo el conteo: es la versión
   * liviana del listado, para el contador que se refresca periódicamente.
   * GET /caja/movimientos/pendientes/count
   */
  async contarMovimientosPendientes(): Promise<number> {
    const { data } = await axiosClient.get<number>('/caja/movimientos/pendientes/count');
    return data;
  },

  /**
   * Movimientos resueltos recientemente (APROBADO/RECHAZADO) de turnos aún
   * abiertos, para poder deshacer una resolución si el propietario se equivocó
   * GET /caja/movimientos/resueltos-recientes
   */
  async listarMovimientosResueltos(page: number = 0, sort?: string): Promise<MovimientoListResponse> {
    const params = new URLSearchParams({ page: page.toString() });
    if (sort) params.append('sort', sort);
    const { data } = await axiosClient.get<MovimientoListResponse>(`/caja/movimientos/resueltos-recientes?${params.toString()}`);
    return data;
  },

  /**
   * Aprobar un movimiento pendiente (solo propietario)
   * POST /caja/movimientos/{id}/aprobar
   */
  async aprobarMovimiento(movimientoId: number): Promise<void> {
    await axiosClient.post(`/caja/movimientos/${movimientoId}/aprobar`);
  },

  /**
   * Rechazar un movimiento pendiente (solo propietario)
   * POST /caja/movimientos/{id}/rechazar
   */
  async rechazarMovimiento(movimientoId: number, dto: RechazarMovimientoDto): Promise<void> {
    await axiosClient.post(`/caja/movimientos/${movimientoId}/rechazar`, dto);
  },

  /**
   * Deshacer una resolución (aprobación o rechazo) y volver el movimiento a
   * PENDIENTE. Solo posible mientras el turno siga abierto (solo propietario)
   * POST /caja/movimientos/{id}/deshacer
   */
  async deshacerResolucionMovimiento(movimientoId: number): Promise<void> {
    await axiosClient.post(`/caja/movimientos/${movimientoId}/deshacer`);
  },

  /**
   * Auditoría de "deshacer": qué resolución tenía cada movimiento antes y
   * quién la deshizo (solo propietario)
   * GET /caja/movimientos/deshacer-log
   */
  async listarMovimientosDeshacerLog(page: number = 0, sort?: string): Promise<MovimientoDeshacerLogListResponse> {
    const params = new URLSearchParams({ page: page.toString() });
    if (sort) params.append('sort', sort);
    const { data } = await axiosClient.get<MovimientoDeshacerLogListResponse>(`/caja/movimientos/deshacer-log?${params.toString()}`);
    return data;
  },
};
